import { Component, Input, Output, EventEmitter, forwardRef, AfterViewInit, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, FormArray, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdminService } from '@app/admin/admin.service';
import { ToastrService } from 'ngx-toastr';
import Quill from 'quill';
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

const icons = Quill.import('ui/icons');
icons['htmlSource'] = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="5 4 1 9 5 14"></polyline><polyline class="ql-stroke" points="13 4 17 9 13 14"></polyline></svg>';

@Component({
    selector: 'app-quill-editor',
    templateUrl: './quill-editor.component.html',
    styleUrls: ['./quill-editor.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuillEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class QuillEditorComponent implements AfterViewInit, OnInit, OnChanges, ControlValueAccessor {
  constructor(private adminSrvc: AdminService, private toastr: ToastrService) {}
  @ViewChild('editorContainer') editorContainer!: ElementRef;
  @Input() formControlName!: string;   // Dynamically set the formControlName
  @Input() formGroup!: FormGroup;      // Parent form group
  @Input() formArrayName: string = '';  // Added formArrayName for conditional logic
  @Input() formIndex: number = -1;     // Added formIndex for conditional logic
  @Output() contentChange = new EventEmitter<any>(); // Event emitter for form control updates
  
  editor: Quill | undefined;
  value: any;
  onChange = (_: any) => {};
  onTouched = () => {};

  toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    ['link', 'formula'],
    [{ 'header': 1 }, { 'header': 2 }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'align': [] }],
    ['clean'],
    ['htmlSource']
  ];

  showHtmlSourceModal = false;
  htmlSourceContent = '';

  ngOnInit(): void {
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editor) {
      return;
    }

    if (changes.formControlName || changes.value) {
      const initialContent = this.formGroup.get(this.formControlName)?.value || this.value || '';
      const delta = this.editor.clipboard.convert(initialContent);
      this.editor.setContents(delta, 'silent');
    }
  }

  showImageWarning(): void {
    this.toastr.warning(
      'Images and videos cannot be added directly to the text editor. Please use the attachments section to add media.',
      'Warning',
      { timeOut: 5000, closeButton: true }
    );
  }

  imageHandler() {
    this.showImageWarning();
  }

  openHtmlSource(): void {
    if (!this.editor) {
      return;
    }
    this.htmlSourceContent = this.editor.root.innerHTML;
    this.showHtmlSourceModal = true;
  }

  applyHtmlSource(): void {
    if (!this.editor) {
      return;
    }
    if (this.htmlSourceContent.includes('<img') || this.htmlSourceContent.includes('<iframe') || this.htmlSourceContent.includes('<video')) {
      this.showImageWarning();
      return;
    }
    const delta = this.editor.clipboard.convert({ html: this.htmlSourceContent });
    this.editor.setContents(delta, 'silent');
    this.updateFormControl();
    this.showHtmlSourceModal = false;
  }

  cancelHtmlSource(): void {
    this.showHtmlSourceModal = false;
  }


  ngAfterViewInit(): void {
    // Initialize Quill editor when the view is ready
    this.editor = new Quill(this.editorContainer.nativeElement, {
      theme: "snow", // Use the 'snow' theme
      modules: {
        toolbar: {
          container: this.toolbarOptions,
          handlers: {
            image: () => this.imageHandler(),
            htmlSource: () => this.openHtmlSource()
          }
        },
        imageResize: {
          modules: ['Resize', 'DisplaySize', 'Toolbar']
        }
      }
    });

    this.editor.root.addEventListener('paste', (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      let hasDisallowed = false;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1 || items[i].type.indexOf('video') !== -1) {
            hasDisallowed = true;
            break;
          }
        }
      }
      const html = e.clipboardData?.getData('text/html');
      if (hasDisallowed || (html && (html.includes('<img') || html.includes('<iframe') || html.includes('<video')))) {
        e.preventDefault();
        this.showImageWarning();
      }
    });

    this.editor.root.addEventListener('drop', (e: DragEvent) => {
      const files = e.dataTransfer?.files;
      let hasDisallowed = false;
      if (files) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') !== -1 || files[i].type.indexOf('video') !== -1) {
            hasDisallowed = true;
            break;
          }
        }
      }
      const html = e.dataTransfer?.getData('text/html');
      if (hasDisallowed || (html && (html.includes('<img') || html.includes('<iframe') || html.includes('<video')))) {
        e.preventDefault();
        this.showImageWarning();
      }
    });

    const htmlSourceButton = this.editorContainer.nativeElement.parentElement?.querySelector('.ql-htmlSource');
    if (htmlSourceButton) {
      htmlSourceButton.setAttribute('title', 'View HTML Source');
    }

    // Bind the editor content to the form control
    this.editor.on('text-change', () => {
      if (this.editor) {
        const html = this.editor.root.innerHTML;
        if (html.includes('<img') || html.includes('<iframe') || html.includes('<video') || html.includes('class="ql-video"')) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const imgs = doc.querySelectorAll('img');
          const iframes = doc.querySelectorAll('iframe, video, .ql-video');
          
          let removed = false;
          imgs.forEach(img => {
            img.remove();
            removed = true;
          });
          iframes.forEach(iframe => {
            iframe.remove();
            removed = true;
          });

          if (removed) {
            const cleanedHtml = doc.body.innerHTML;
            const selection = this.editor.getSelection();
            this.editor.root.innerHTML = cleanedHtml;
            if (selection) {
              this.editor.setSelection(selection.index);
            }
            this.showImageWarning();
          }
        }
      }
      this.updateFormControl();
      const html = this.editor!.root.innerHTML;
      this.value = html;
      this.onChange(html);
      this.onTouched();
    });

    // Set the initial value for the editor if the form control has a value.
    // Routed through setContents(..., 'silent') rather than a direct innerHTML
    // assignment, so Quill's own change-tracking doesn't treat loading existing
    // data as a real edit and fire text-change/contentChange from it.
    if (this.formArrayName && this.formIndex !== -1) {
      const formArray = this.formGroup.get(this.formArrayName) as FormArray;
      if (formArray && formArray.at(this.formIndex)) {
        const formControl = formArray.at(this.formIndex).get(this.formControlName);
        if (formControl?.value) {
          const delta = this.editor.clipboard.convert({ html: formControl.value });
          this.editor.setContents(delta, 'silent');
        }
      }
    } else {
      const initialContent = this.formGroup.get(this.formControlName)?.value || '';
      if (this.editor && initialContent) {
        const delta = this.editor.clipboard.convert({ html: initialContent });
        this.editor.setContents(delta, 'silent');
      }
    }

  }

writeValue(value: any): void {
    this.value = value || '';
    if (!this.editor) {
      return;
    }
    const editorContent = this.editor.root.innerHTML;
    // Initialize editor only if it's empty
    if (!editorContent || editorContent === '<p><br></p>') {
      const delta = this.editor.clipboard.convert(this.value);
      this.editor.setContents(delta, 'silent');
    }
  }

  // Forcefully replaces editor content, bypassing writeValue's empty-only guard.
  setContent(content: string): void {
    this.value = content || '';
    if (!this.editor) {
      return;
    }
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(this.value);
    const html = hasHtmlTags ? this.value : `<p>${this.value.replace(/\n/g, '<br>')}</p>`;
    const delta = this.editor.clipboard.convert({ html });
    this.editor.setContents(delta, 'silent');
    this.updateFormControl();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (this.editor) {
      this.editor.enable(!isDisabled);
    }
  }

  updateFormControl(): void {
    if (this.editor) {
      const content = this.editor.root.innerHTML;
      if (this.formArrayName && this.formIndex !== -1) {
        const formArray = this.formGroup.get(this.formArrayName) as FormArray;
        if (formArray && formArray.at(this.formIndex)) {
          formArray.at(this.formIndex).get(this.formControlName)?.setValue(content);
        }
        this.contentChange.emit({
          index: this.formIndex,
          content: content
        });
      } else {
        this.formGroup.get(this.formControlName)?.setValue(content);
        this.contentChange.emit(content);
      }
    }
  }
}
